/**
 * @pit/sim \u2014 aura runtime (#30).
 *
 * Data model, hashing layout, and design tenets: see
 *   docs/design/aura-system-skeleton.md
 *   docs/wp3-sim-core.md \u00a7"Hashing" (aura section)
 *
 * WHAT THIS FILE OWNS:
 *   \u2022 The runtime step (3c) inserted in `step.ts` between projectiles and
 *     movement:
 *
 *       for each holder in seat order:
 *         for each aura in canonical order:
 *           (a) apply-due-periodic: if `tick > appliedAtTick` and
 *               `imod(tick - appliedAtTick, intervalTicks) === 0`, emit the
 *               `periodic.effect` through `applyEffect` (routed via a
 *               callback to break the auras \u2194 effects import cycle).
 *           (b) expire: if `expiresAtTick !== null && expiresAtTick <= tick`,
 *               drop the aura from the holder.
 *
 *     Order is LOCKED: (a) BEFORE (b) so the expiry tick still fires its
 *     periodic effect (Corruption: 360 total ticks, expiry at t=apply+360
 *     applies the 12th damage AND then removes the aura). See spec Q3
 *     resolution + task brief.
 *
 *   \u2022 `applyAuraInstance` \u2014 stacking-aware attach, called from
 *     `applyEffect(kind:"applyAura", \u2026)`.
 *
 *   \u2022 `isActive(holder, aura, tick)` \u2014 derived every tick; suppressed
 *     auras contribute nothing (all primitives incl. absorb).
 *
 *   \u2022 `internAuraDefs(state)` \u2014 deterministic def-id \u2192 ordinal table
 *     used by the hash. Sorted content-key ids per Q4; the sim NEVER
 *     hashes an aura def string.
 *
 * DETERMINISM: integer ticks only, `imod` from @pit/math (no `%`), no
 * floats, no Math.*, no bitwise. All array ordering is seat-order/holder
 * for holders, and (defOrdinal, sourceSeat, appliedAtTick) for auras.
 */

import { idiv, imod } from "@pit/math";
import type { AbilityId, AuraDef, AuraDefId, AuraPrimitive, Effect, ImmunityScope, SeatIndex, TagSelector, Tick } from "@pit/protocol";
import type { ContentBundle } from "@pit/content";
import { asHp } from "./brand.js";
import { ccClassBreakOnDamageDefault, ccSelectionTokens, hardCcClasses } from "./ccClasses.js";
import type { CombatEventSink } from "./events.js";
import type { AuraInstance, Entity, SimState } from "./types.js";

/** #147: the `forcedMovement` aura primitive, narrowed from the union. */
export type ForcedMovementPrimitive = Extract<AuraPrimitive, { kind: "forcedMovement" }>;

/**
 * Callback signature the aura step uses to route a periodic effect through
 * the shared `applyEffect` pipeline. Provided by `step.ts` so this module
 * doesn't import `effects.ts` (which imports `types.ts`, forming a cycle if
 * we go the other way).
 */
export type ApplyEffectFn = (
  draft: Entity[],
  sourceSeat: number,
  targetSeat: number,
  effect: Effect,
  tick: Tick,
) => void;

// ─── Interning ─────────────────────────────────────────────────────────────

/**
 * Build the def-id \u2192 ordinal table used by `hashState` and by any code
 * that needs to compare/sort auras by their def in an integer-safe way.
 *
 * DETERMINISM: ordinals come from the *sorted set of aura def ids present
 * on ANY entity in `state`*. String ids are never hashed \u2014 same rule as
 * `e{seat}`. Two structurally identical states produce the same table.
 *
 * Sort is a stable lex sort on the def-id strings (JS `Array.prototype.sort`
 * with an explicit compareFn). The sort input contains no duplicates \u2014
 * we build a Set first \u2014 so ordering ties don't exist.
 *
 * Rationale for state-scoped interning (not bundle-scoped): the bundle is
 * not always visible where `hashState` is called (tests, replay runner);
 * requiring it would force ~50 call sites through a new signature. The
 * subset of def ids present in a well-formed state is always a subset of
 * `bundle.auras`, and within a single match the sim runs against ONE
 * bundle, so two peers hashing the same state see the same ordinals.
 */
export function internAuraDefs(state: SimState): Map<string, number> {
  const seen = new Set<string>();
  for (const e of state.entities) {
    for (const a of e.auras) {
      seen.add(a.def);
    }
  }
  const ids: string[] = [];
  for (const id of seen) ids.push(id);
  ids.sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  const out = new Map<string, number>();
  for (let i = 0; i < ids.length; i++) {
    const id = ids[i];
    /* c8 ignore next -- defensive: ids is dense. */
    if (id === undefined) continue;
    out.set(id, i);
  }
  return out;
}

/**
 * Compare two auras in canonical order:
 *   (interned def ordinal, sourceSeat, appliedAtTick).
 *
 * `ordinals` is the state-scoped interning table from `internAuraDefs`.
 * Both auras' def ids MUST be in the table (they came from the state we
 * interned).
 */
export function compareAuras(
  a: AuraInstance,
  b: AuraInstance,
  ordinals: Map<string, number>,
): number {
  const oa = ordinals.get(a.def);
  const ob = ordinals.get(b.def);
  /* c8 ignore next 4 -- defensive: caller always interns from the same state. */
  if (oa === undefined || ob === undefined) {
    if (a.def < b.def) return -1;
    if (a.def > b.def) return 1;
  } else if (oa !== ob) {
    return oa - ob;
  }
  if (a.sourceSeat !== b.sourceSeat) return a.sourceSeat - b.sourceSeat;
  return a.appliedAtTick - b.appliedAtTick;
}

/** Return a new list sorted in canonical order. Does not mutate input. */
function sortAuras(
  auras: readonly AuraInstance[],
  ordinals: Map<string, number>,
): AuraInstance[] {
  const out: AuraInstance[] = [];
  for (const a of auras) out.push(a);
  out.sort((x, y) => compareAuras(x, y, ordinals));
  return out;
}

/**
 * #103: return the indices into `holder.auras` in the SAME canonical order the
 * hash walks — (interned def ordinal, sourceSeat, appliedAtTick). The ordinal
 * table is built from `draft` via the IDENTICAL state-scoped interning the
 * hasher uses (`buildOrdinalsFromDraft` ≡ `internAuraDefs`), so a caller that
 * fans out over one holder's auras (the shield-drain pre-step in `applyEffect`)
 * walks them byte-for-byte in hash order. Determinism note: ordinals preserve
 * the lex order of def-id strings, so the RELATIVE order of a holder's auras is
 * invariant to whatever other defs exist elsewhere in `draft` — the drain order
 * is exactly the order those same auras appear under in `hashState`.
 *
 * Returns INDICES (not instances) so the caller can mutate + write back a
 * single instance in its ORIGINAL array slot without reordering the stored
 * array (array order across instances is not hashed — the hasher re-sorts —
 * but keeping it stable avoids needless churn). Pure read; never mutates.
 */
export function canonicalAuraOrder(
  holder: Entity,
  draft: readonly Entity[],
): number[] {
  const ordinals = buildOrdinalsFromDraft(draft);
  const idx: number[] = [];
  for (let i = 0; i < holder.auras.length; i++) idx.push(i);
  idx.sort((a, b) => {
    const aa = holder.auras[a];
    const bb = holder.auras[b];
    /* c8 ignore next -- defensive: indices come from holder.auras own length. */
    if (aa === undefined || bb === undefined) return 0;
    return compareAuras(aa, bb, ordinals);
  });
  return idx;
}

// ─── Forced movement (#147) ────────────────────────────────────────────────

/**
 * #147: find the forced-movement primitive currently DRIVING `holder`, if any.
 * Walks the holder's auras in the SAME canonical order the hash uses
 * (`canonicalAuraOrder`) and returns the FIRST ACTIVE aura that carries a
 * `forcedMovement` primitive — together with that primitive and the aura's
 * ARRAY index, so the movement authority can write an advanced `fixedPath`
 * cursor back in place. Canonical-order-first makes "which fear wins when
 * several forced auras are active" fully deterministic. Returns `null` when no
 * active forced-movement aura holds (or the holder is `forcedMove`-immune, #142).
 * Pure read; never mutates.
 */
export function governingForcedMovement(
  holder: Entity,
  draft: readonly Entity[],
  tick: Tick,
  bundle: ContentBundle,
): { readonly auraIndex: number; readonly prim: ForcedMovementPrimitive } | null {
  if (holder.auras.length === 0) return null;
  // #142: a holder immune to forced movement (`immunity { all: "forcedMove" }`)
  // is NEVER driven — fear/charm/disorient auras stay attached but inert, so the
  // holder keeps its own move intent this tick (and resumes being driven if the
  // immunity later expires). Gated here so the step.ts authority leaves the seat
  // un-rooted. The DISPLACE effect's twin gate lives in `effects.ts` scopeBlocks.
  if (hasActiveImmunityScope(holder, "forcedMove", tick, bundle)) return null;
  for (const i of canonicalAuraOrder(holder, draft)) {
    const a = holder.auras[i];
    /* c8 ignore next -- index from canonicalAuraOrder ⇒ in-bounds. */
    if (a === undefined) continue;
    if (!isActive(holder, a, tick, bundle)) continue;
    const def = bundle.auras.get(a.def);
    /* c8 ignore next -- content invariant: applyAura validated the def. */
    if (def === undefined) continue;
    for (const prim of def.primitives) {
      if (prim.kind === "forcedMovement") {
        return { auraIndex: i, prim };
      }
    }
  }
  return null;
}

/**
 * #142: does `holder` currently hold an ACTIVE aura granting an absolute
 * `immunity` primitive whose `all` scope equals `scope`? The scope-keyed twin of
 * `effects.ts` `targetImmuneTo` (which is EFFECT-keyed) — used where there is no
 * `Effect` object to test against, i.e. the `forcedMove` gate on the sustained
 * `forcedMovement` aura drive (#147). Pure read of hashed aura state (active +
 * unsuppressed); no mutation, no float. Mirrors the immunity walk in
 * `targetImmuneTo` so the two never disagree about "is this scope active".
 */
export function hasActiveImmunityScope(
  holder: Entity,
  scope: ImmunityScope,
  tick: Tick,
  bundle: ContentBundle,
): boolean {
  for (const aura of holder.auras) {
    if (!isActive(holder, aura, tick, bundle)) continue;
    const def = bundle.auras.get(aura.def);
    /* c8 ignore next -- content invariant: applyAura validated the def. */
    if (def === undefined) continue;
    for (const prim of def.primitives) {
      if (prim.kind !== "immunity") continue;
      if (prim.all === scope) return true;
    }
  }
  return false;
}

/**
 * #669: find the FIRST active aura instance (canonical order) on `holder`
 * carrying a `nextCastInstant` primitive whose `abilityId` matches
 * `abilityId` — the "Getting Hot → next Fireball is instant" query. Mirrors
 * `hasActiveImmunityScope`'s shape, but returns the matching INSTANCE (not a
 * bare boolean): the cast pipeline (`cast.ts`'s `evaluateCastStart`) must
 * CONSUME (remove) it on a match — a one-shot proc, not a passive modifier.
 * Canonical order makes the pick deterministic in the (unlikely) case more
 * than one active instance matches. Pure read; no mutation.
 */
export function findNextCastInstantAura(
  holder: Entity,
  draft: readonly Entity[],
  abilityId: AbilityId,
  tick: Tick,
  bundle: ContentBundle,
): AuraInstance | null {
  if (holder.auras.length === 0) return null;
  for (const i of canonicalAuraOrder(holder, draft)) {
    const a = holder.auras[i];
    /* c8 ignore next -- index from canonicalAuraOrder ⇒ in-bounds. */
    if (a === undefined) continue;
    if (!isActive(holder, a, tick, bundle)) continue;
    const def = bundle.auras.get(a.def);
    /* c8 ignore next -- content invariant: applyAura validated the def. */
    if (def === undefined) continue;
    for (const prim of def.primitives) {
      if (prim.kind === "nextCastInstant" && prim.abilityId === abilityId) return a;
    }
  }
  return null;
}

/**
 * #622: does `holder` currently hold an ACTIVE aura that is BOTH (a) `hardCC`-
 * classed (`stun`/`mez`/`fear` — `CC_ROLLUP_TABLE`'s `hardCC` rollup, tested
 * via the same `ccSelectionTokens` membership every dispel/immunity selector
 * uses) AND (b) carries a positive EFFECTIVE `breakOnDamageThreshold` (the
 * def's own explicit field, falling back to its `ccClasses`' shared tuning
 * default — the identical effective-threshold rule `accumulateBreakOnDamage`
 * applies, so this can never disagree with what actually breaks the CC)?
 *
 * Used by the auto-attack swing gate (#622) to suppress the ATTACKER's own
 * auto from being the thing that breaks a hard-CC effect it (or an ally) just
 * landed on its target — a footgun/griefing-prevention rule. Deliberately
 * gated on the break-on-damage clause, not bare `hardCC` membership: a hard
 * stun authoring no break-on-damage rule doesn't care about damage, so
 * suppressing the auto there would be a pure DPS loss with no benefit. Reads
 * the target's LIVE aura state (no polymorph-specific special case), so any
 * current or future breaking hard-CC aura is covered automatically.
 *
 * Pure read; no mutation, integer-only. Walks the SAME canonical order every
 * other aura fan-out uses (`canonicalAuraOrder`) for consistency, though this
 * is a pure existence check so the iteration order cannot change the outcome.
 */
export function hasBreakingHardCc(
  holder: Entity,
  draft: readonly Entity[],
  tick: Tick,
  bundle: ContentBundle,
): boolean {
  if (holder.auras.length === 0) return false;
  for (const i of canonicalAuraOrder(holder, draft)) {
    const a = holder.auras[i];
    /* c8 ignore next -- index from canonicalAuraOrder ⇒ in-bounds. */
    if (a === undefined) continue;
    if (!isActive(holder, a, tick, bundle)) continue;
    const def = bundle.auras.get(a.def);
    /* c8 ignore next -- content invariant: applyAura validated the def. */
    if (def === undefined) continue;
    if (!ccSelectionTokens(def.ccClasses).includes("hardCC")) continue;
    const threshold =
      def.breakOnDamageThreshold ?? ccClassBreakOnDamageDefault(def.ccClasses, bundle.tuning);
    if (threshold > 0) return true;
  }
  return false;
}

// ─── Suppression / isActive ────────────────────────────────────────────────

/**
 * Does `selector` match `tags`? Content selectors are `{ kind: "any"|"all",
 * tags: string[] }`. `any` \u2014 at least one match. `all` \u2014 every selector
 * tag present in the aura's tag list. Never throws on empty (schema
 * guarantees selector.tags.length \u2265 1).
 *
 * Exported so the `dispel` effect (#135) selects auras through the SAME
 * predicate suppression (#30) uses — a dispel and a suppress can never disagree
 * about "does this aura match this selector".
 */
export function selectorMatches(selector: TagSelector, tags: readonly string[]): boolean {
  const set = new Set<string>(tags);
  if (selector.kind === "any") {
    for (const t of selector.tags) {
      if (set.has(t)) return true;
    }
    return false;
  }
  // "all"
  for (const t of selector.tags) {
    if (!set.has(t)) return false;
  }
  return true;
}

/**
 * #142: the complete SELECTION TOKEN set a `dispel`/`immunity`/`suppresses` tag
 * selector matches an aura against — the def's free-form `tags` (#30) PLUS the
 * CC-class tokens its `ccClasses` contribute (each class value + the rollup
 * names it belongs to; see `ccSelectionTokens`). Deriving the CC tokens HERE —
 * rather than making content re-tag every CC aura by hand — keeps the closed
 * `ccClasses` enum the SINGLE source of truth for CC selection (a slow authored
 * as `ccClasses: ["slow"]` is dispelled by `{tags:["slow"]}` or
 * `{tags:["movementImpair"]}` with no free-form tag). crossValidate rejects a
 * free-form tag that spells a CC class/rollup, so the two token namespaces never
 * silently overlap. Pure; no mutation, no float.
 */
export function auraSelectionTokens(def: AuraDef): string[] {
  const cc = ccSelectionTokens(def.ccClasses);
  if (cc.length === 0) return def.tags.slice();
  return [...def.tags, ...cc];
}

/**
 * #76: does `entity` hold an UNEXPIRED aura instance with well-known def id
 * `defId`, at `tick`? A pure, BUNDLE-FREE read of hashed state (an
 * `AuraInstance` carries only its def id, not its tags, so this checks the id
 * directly) — the SAME expiry-only rule `isActive` uses, minus the
 * suppression check (mirrors `targeting.ts`'s pre-#76 `isStealthed`, which
 * this generalizes). Used by `resolveSlotAbility`'s `holdsAura` condition
 * (#76) and by `isStealthed` below.
 */
export function hasActiveAuraDef(entity: Entity, defId: AuraDefId, tick: Tick): boolean {
  for (const a of entity.auras) {
    if (a.def !== defId) continue;
    if (a.expiresAtTick === null || tick < a.expiresAtTick) return true;
  }
  return false;
}

/**
 * Is `aura` active on `holder` at `tick`?
 *
 * Active iff:
 *   - not expired (`expiresAtTick === null || tick < expiresAtTick`),
 *     UNLESS the caller is the aura step itself \u2014 the aura step applies
 *     the boundary tick's periodic effect BEFORE expiry, so passing
 *     `treatExpiryTickAsActive: true` allows the equality case.
 *   - not suppressed by any OTHER active aura's `suppresses` selector
 *     matching one of this aura's tags.
 *
 * Suppression is derived every tick; never stored (avoids desync between
 * the stored bool and the suppressor set). Corruption doesn't use
 * suppression, but the helper honors it \u2014 future auras plug in without
 * touching call sites.
 */
export function isActive(
  holder: Entity,
  aura: AuraInstance,
  tick: Tick,
  bundle: ContentBundle,
): boolean {
  if (aura.expiresAtTick !== null && tick >= aura.expiresAtTick) return false;
  return !isSuppressed(holder, aura, tick, bundle);
}

/**
 * Is `aura` suppressed by any other active-and-not-suppressing-itself
 * aura on `holder` at `tick`?
 *
 * Terminates: we only check other auras' `suppresses` selector against
 * THIS aura's tags. The suppressor's own tags are not re-checked, so
 * mutual-suppression pairs don't recurse.
 */
export function isSuppressed(
  holder: Entity,
  aura: AuraInstance,
  tick: Tick,
  bundle: ContentBundle,
): boolean {
  const myDef = bundle.auras.get(aura.def);
  /* c8 ignore next -- content invariant: applyAura validated the def. */
  if (myDef === undefined) return false;
  for (const other of holder.auras) {
    if (other === aura) continue;
    // Un-expired suppressors only.
    if (other.expiresAtTick !== null && tick >= other.expiresAtTick) continue;
    const otherDef = bundle.auras.get(other.def);
    if (otherDef === undefined) continue;
    if (otherDef.suppresses === undefined) continue;
    // #142: match against the FULL selection-token set (tags + CC-class tokens),
    // so an aura that suppresses a CC class (`suppresses: {tags:["slow"]}`) can
    // select it exactly like a dispel does — one predicate, one token namespace.
    if (selectorMatches(otherDef.suppresses, auraSelectionTokens(myDef))) return true;
  }
  return false;
}

// ─── Stacking / applyAuraInstance ─────────────────────────────────────────

/**
 * Deterministic "strength" of an aura for the `strongestWins` stacking
 * rule. Currently derived from `stacks` (higher wins), with the docstring's
 * documented tie-break (lowest sourceSeat) applied in `applyAuraInstance`.
 *
 * Corruption uses `refresh`, so this only executes for future
 * strongest-wins auras \u2014 kept isolated so its criterion evolves without
 * touching the attach code.
 */
function strength(aura: AuraInstance): number {
  return aura.stacks;
}

/**
 * Compute the appliedAt / expiresAt pair for a fresh aura instance.
 * `durationTicks === "permanent"` \u21d2 expiresAtTick = null.
 *
 * #448: `durationTicks === "scaledOnly"` means the def carries NO authored
 * base lifetime \u2014 the caller MUST already have overridden `durationTicks` to
 * a concrete number (a shallow def copy) BEFORE calling
 * `applyAuraInstance`/`computeExpiry`. Two callers do this today: the `stun`
 * effect (`{...stunDef, durationTicks: effect.durationTicks}`) and, since
 * #447, the generic `applyAura` effect when it carries a
 * `durationPerPointTable` (`{...def, durationTicks: base + tableLookup}`,
 * where `base` is 0 for a `scaledOnly` def \u2014 see `effects.ts`'s `applyAura`
 * case). crossValidate forbids EVERY OTHER path from referencing a
 * `scaledOnly` def, so a raw `"scaledOnly"` reaching this function is
 * unreachable at runtime \u2014 defensive branch only.
 */
function computeExpiry(def: AuraDef, appliedAtTick: Tick): Tick | null {
  if (def.durationTicks === "permanent") return null;
  /* c8 ignore next 3 -- content invariant: crossValidate rejects every
     `applyAura` reference to a `scaledOnly` def EXCEPT one carrying
     `durationPerPointTable`, which always overrides `durationTicks` to a
     number first (mirrors the `stun` effect's own override). */
  if (def.durationTicks === "scaledOnly") return appliedAtTick;
  // NB: expiresAtTick = appliedAtTick + durationTicks. The aura step
  // applies its periodic effect AT the expiry tick then removes the
  // aura (see file header). Corruption: apply@0 \u21d2 expiry@360, 12 ticks
  // fired at t=30,60,\u2026,360 inclusive.
  const expiry = appliedAtTick + def.durationTicks;
  return expiry as Tick;
}

/**
 * Attach `def` to `holder` at `tick`, honoring the def's stacking rule.
 * Returns the new auras list for the holder (immutable-friendly \u2014 caller
 * copies the entity with `{ ...holder, auras: next }` and writes back).
 *
 * Stacking:
 *   \u2022 refresh              \u2014 if any existing instance of the same def,
 *                            replace its (appliedAtTick, expiresAtTick).
 *                            Otherwise attach fresh.
 *   \u2022 stack (max: N)       \u2014 if any existing instance of the same def
 *                            with `stacks < max`, increment stacks and
 *                            reset (appliedAt, expiresAt). If already at
 *                            max, refresh timing only (stacks capped).
 *                            No same-def instance \u21d2 attach fresh.
 *   \u2022 strongestWins        \u2014 keep the strongest existing instance of the
 *                            same def; ties break by LOWEST sourceSeat.
 *                            Incoming loses \u21d2 no change; incoming wins \u21d2
 *                            replaces the loser (removes it, attaches
 *                            fresh).
 *   \u2022 independentPerSource \u2014 one instance per (def, sourceSeat). Same-
 *                            source reapply \u21d2 refresh timing on that
 *                            instance. Different source \u21d2 new instance.
 *   \u2022 independentInstances \u2014 (#218) EVERY application from a source mints a
 *                            FRESH instance with its own timer (never refreshes),
 *                            so N same-source DoTs run N independent expiries.
 *                            Cross-caster uncapped; WITHIN a (def, sourceSeat)
 *                            group the count is capped at `maxInstances`, and an
 *                            overflow application EVICTS the oldest same-source
 *                            instance first (oldest = lowest appliedAtTick, tie
 *                            \u2192 lowest array index \u2014 a deterministic, replay-safe
 *                            choice; same-tick same-source instances are byte-
 *                            identical so the choice never perturbs the hash).
 *
 * `absorbRemaining` seeds from the def's absorbPool primitive amount
 * (if any). Corruption has none \u2192 0.
 *
 * `pointsSnapshot` (#320) governs a periodic `snapshotScaling` primitive's
 * per-tick magnitude (see `AuraInstance.pointsSnapshot`). Every branch that
 * REFRESHES an existing instance's timing (refresh / stack / independentPer-
 * Source) also re-captures the incoming `pointsSnapshot` onto that instance —
 * this cast's parameters govern, the SAME treatment `expiresAtTick` already
 * gets from `durationPerPoint`. `strongestWins`' losing branch and a fresh
 * `independentInstances` attach behave exactly as their existing fields do
 * (untouched / freshly seeded, respectively).
 */
export function applyAuraInstance(
  holder: Entity,
  def: AuraDef,
  sourceSeat: SeatIndex,
  tick: Tick,
  /** #182: override the FRESH instance's initial `absorbRemaining` with this
   *  exact runtime value (the zone-collapse bolt seeds its reservoir DoT with
   *  the well's accumulated pool). Absent ⇒ seed from the def's `absorbPool`
   *  primitives (byte-identical to pre-#182). Only sizes a fresh ATTACH — a
   *  refresh/stack of an existing instance keeps its own live balance. */
  absorbSeedOverride?: number,
  /** #320: the combo-point balance to SNAPSHOT onto this (re)application —
   *  the applying source's LIVE `points.current`, read by the caller BEFORE
   *  any same-cast `spendPoints` effect zeroes it (same read-then-mutate
   *  discipline as `durationPerPoint`/`perPointBonus`). Absent ⇒ 0 (byte-
   *  identical to pre-#320 content: every existing call site that never
   *  passes this argument keeps hashing a stable 0). */
  pointsSnapshot?: number,
): readonly AuraInstance[] {
  const expiresAtTick = computeExpiry(def, tick);
  const snapshot = pointsSnapshot ?? 0;
  const fresh: AuraInstance = {
    def: def.id,
    sourceSeat,
    appliedAtTick: tick,
    expiresAtTick,
    stacks: 1,
    absorbRemaining: absorbSeedOverride ?? initialAbsorbAmount(def),
    // #55: one zeroed counter per `trigger` primitive, in def-primitive
    // order. A refresh/stack of an existing instance KEEPS its counters
    // (the instance persists) — only a fresh attach starts at zero.
    triggerCounts: initialTriggerCounts(def),
    // #118 (Foundation A): fresh instance has taken no damage yet. The damage
    // arm credits actual hp loss here; a refresh/stack of an existing instance
    // KEEPS its accumulator (the instance persists — those branches spread
    // `...a`), so only a fresh attach starts at zero (matches triggerCounts).
    damageAccum: 0,
    // #147: no forced-movement cursor on attach. The forced-movement authority
    // (step.ts) snapshots the origin + seeds the cursor the first tick a
    // `fixedPath` primitive on this def drives the holder; `awayFromSource`/
    // `towardSource` never set it. A refresh/stack spreads `...a` and keeps any
    // live cursor.
    forcedPath: null,
    // #320: cast-time combo-point snapshot for a periodic `snapshotScaling`
    // primitive. A fresh attach always seeds this from the CURRENT cast.
    pointsSnapshot: snapshot,
  };

  switch (def.stacking.kind) {
    case "refresh": {
      const next: AuraInstance[] = [];
      let refreshed = false;
      for (const a of holder.auras) {
        if (!refreshed && a.def === def.id) {
          next.push({ ...a, appliedAtTick: tick, expiresAtTick, pointsSnapshot: snapshot });
          refreshed = true;
        } else {
          next.push(a);
        }
      }
      if (!refreshed) next.push(fresh);
      return next;
    }
    case "stack": {
      const max = def.stacking.max;
      const next: AuraInstance[] = [];
      let matched = false;
      for (const a of holder.auras) {
        if (!matched && a.def === def.id) {
          const nextStacks = a.stacks < max ? a.stacks + 1 : max;
          next.push({
            ...a,
            appliedAtTick: tick,
            expiresAtTick,
            stacks: nextStacks,
            pointsSnapshot: snapshot,
          });
          matched = true;
        } else {
          next.push(a);
        }
      }
      if (!matched) next.push(fresh);
      return next;
    }
    case "strongestWins": {
      // Look for an existing same-def instance; compare strengths.
      let existingIdx = -1;
      for (let i = 0; i < holder.auras.length; i++) {
        const a = holder.auras[i];
        if (a === undefined) continue;
        if (a.def === def.id) {
          existingIdx = i;
          break;
        }
      }
      if (existingIdx < 0) {
        const next: AuraInstance[] = [];
        for (const a of holder.auras) next.push(a);
        next.push(fresh);
        return next;
      }
      const existing = holder.auras[existingIdx];
      /* c8 ignore next -- defensive: existingIdx came from a hit. */
      if (existing === undefined) return holder.auras;
      const sIncoming = strength(fresh);
      const sExisting = strength(existing);
      // Incoming wins iff strictly stronger, OR equal strength AND lower
      // sourceSeat (task-brief locked tie-break).
      const incomingWins =
        sIncoming > sExisting ||
        (sIncoming === sExisting && sourceSeat < existing.sourceSeat);
      if (!incomingWins) return holder.auras;
      const next: AuraInstance[] = [];
      for (let i = 0; i < holder.auras.length; i++) {
        if (i === existingIdx) continue;
        const a = holder.auras[i];
        /* c8 ignore next -- defensive: dense. */
        if (a === undefined) continue;
        next.push(a);
      }
      next.push(fresh);
      return next;
    }
    case "independentPerSource": {
      const next: AuraInstance[] = [];
      let refreshed = false;
      for (const a of holder.auras) {
        if (!refreshed && a.def === def.id && a.sourceSeat === sourceSeat) {
          next.push({ ...a, appliedAtTick: tick, expiresAtTick, pointsSnapshot: snapshot });
          refreshed = true;
        } else {
          next.push(a);
        }
      }
      if (!refreshed) next.push(fresh);
      return next;
    }
    case "independentInstances": {
      // #218: never refresh — every application is a FRESH instance with its own
      // timer. Cap the (def, sourceSeat) group at `maxInstances`, evicting the
      // OLDEST same-source instances first (oldest = lowest appliedAtTick, tie →
      // lowest array index) so the newest DoT always lands. Different sources are
      // uncapped (each keeps its own group).
      const cap = def.stacking.maxInstances;
      // Indices of existing same-(def, source) instances, gathered in array order.
      const groupIdx: number[] = [];
      for (let i = 0; i < holder.auras.length; i++) {
        const a = holder.auras[i];
        /* c8 ignore next -- defensive: holder.auras is dense. */
        if (a === undefined) continue;
        if (a.def === def.id && a.sourceSeat === sourceSeat) groupIdx.push(i);
      }
      // Evict enough existing instances so the group ends at exactly `cap` after
      // the fresh one is added: evict = (existing + 1) − cap, only when positive.
      const overflow = groupIdx.length + 1 - cap;
      const evict = new Set<number>();
      if (overflow > 0) {
        // Oldest-first: sort the group by (appliedAtTick asc, array index asc)
        // and mark the first `overflow` for eviction. `groupIdx` is already in
        // ascending array-index order, so the sort's index tiebreak is stable.
        const ordered = groupIdx.slice().sort((ia, ib) => {
          const aa = holder.auras[ia];
          const ab = holder.auras[ib];
          /* c8 ignore next -- indices came from holder.auras itself. */
          if (aa === undefined || ab === undefined) return 0;
          if (aa.appliedAtTick !== ab.appliedAtTick) return aa.appliedAtTick - ab.appliedAtTick;
          return ia - ib;
        });
        for (let k = 0; k < overflow && k < ordered.length; k++) {
          const idx = ordered[k];
          /* c8 ignore next -- k < ordered.length ⇒ idx defined. */
          if (idx !== undefined) evict.add(idx);
        }
      }
      const next: AuraInstance[] = [];
      for (let i = 0; i < holder.auras.length; i++) {
        if (evict.has(i)) continue;
        const a = holder.auras[i];
        /* c8 ignore next -- defensive: dense. */
        if (a === undefined) continue;
        next.push(a);
      }
      next.push(fresh);
      return next;
    }
  }
}

/**
 * Seed `absorbRemaining` from the def's absorbPool primitive if present.
 * Multiple absorbPool primitives on one def sum \u2014 unusual, but well-
 * defined; a single primitive is the common case.
 *
 * The seeded balance is DRAINED by incoming damage (#103): the `damage` case
 * in `effects.ts` soaks the hit against every active shield in canonical aura
 * order BEFORE writing hp. `schoolFilter` is a no-op at v1 (schools deferred,
 * #100) \u2014 a shield absorbs ALL damage, and crossValidate rejects a non-empty
 * filter so no content silently relies on the unbuilt school lockout.
 */
function initialAbsorbAmount(def: AuraDef): number {
  let total = 0;
  for (const p of def.primitives) {
    if (p.kind === "absorbPool") total += p.amount;
  }
  return total;
}

/**
 * #182: does `def` carry a RESERVOIR-mode `absorbPool`? A reservoir pool does
 * NOT soak incoming damage (the shield drain in `effects.ts` SKIPS a def with
 * one) — instead its `absorbRemaining` FUNDS a `periodic` primitive on the same
 * def, drained each firing (`reservoirPerTick`). A def carrying ONLY shield
 * pools (the #103 default) returns false and behaves exactly as before.
 * crossValidate pins a reservoir def to exactly one absorbPool + a periodic
 * damage/heal, so the "first reservoir pool wins" read here is unambiguous.
 */
export function hasReservoirPool(def: AuraDef): boolean {
  for (const p of def.primitives) {
    if (p.kind === "absorbPool" && p.mode === "reservoir") return true;
  }
  return false;
}

/**
 * #182: the per-firing magnitude a RESERVOIR-funded periodic pays out, and the
 * pool balance AFTER that payout. `firingsLeft` (this firing inclusive) =
 * `idiv(expiresAtTick - tick, interval) + 1`; the payout drains an EQUAL share
 * `idiv(absorbRemaining, firingsLeft)` so the pool reaches exactly 0 on the
 * final firing — the total is conserved with NO stored total / firing index (a
 * genuine draining pool, not a hidden budget). Integer-only. Only called on a
 * firing tick (`firingsLeft >= 1`), so no divide-by-zero.
 */
export function reservoirPerTick(
  aura: AuraInstance,
  intervalTicks: number,
  tick: Tick,
): { readonly perTick: number; readonly nextRemaining: number } {
  const expiry = aura.expiresAtTick;
  // A permanent reservoir has no bounded window (crossValidate rejects it), but
  // guard defensively: pay out nothing, keep the pool.
  /* c8 ignore next -- crossValidate pins reservoir DoTs to a numeric duration. */
  if (expiry === null) return { perTick: 0, nextRemaining: aura.absorbRemaining };
  const firingsLeft = idiv(expiry - tick, intervalTicks) + 1;
  const perTick = idiv(aura.absorbRemaining, firingsLeft);
  return { perTick, nextRemaining: aura.absorbRemaining - perTick };
}

/**
 * Build the initial event-counter array (#55, #43): one zeroed entry per
 * COUNTED primitive on the def, in DEF-PRIMITIVE ORDER. A counted primitive is
 * one that fires on an every-Nth-event cadence: `trigger` (#55) and
 * `reflectProjectile` (#43). An aura with no counted primitive gets `[]`.
 *
 * Order MUST match the iteration order every consumer walks (def.primitives
 * order, advancing the counter index past each counted primitive) so counter
 * index ↔ primitive stay aligned — see `isCountedPrimitive` (the single source
 * of truth for which kinds get a slot), `dispatchTriggers` (#55), and
 * `resolveProjectileReflect` (#43).
 */
function initialTriggerCounts(def: AuraDef): readonly number[] {
  const out: number[] = [];
  for (const p of def.primitives) {
    if (isCountedPrimitive(p.kind)) out.push(0);
  }
  return out;
}

/**
 * Does an aura primitive of this kind carry an every-Nth event counter (a slot
 * in `AuraInstance.triggerCounts`)? The ONE place the counted-primitive set is
 * defined — both the trigger dispatch (#55) and the projectile-reflect resolve
 * (#43) walk `def.primitives` advancing a shared counter index, and MUST agree
 * with `initialTriggerCounts` on which kinds consume a slot. Add a new counted
 * kind here and every walker stays aligned.
 */
export function isCountedPrimitive(kind: AuraDef["primitives"][number]["kind"]): boolean {
  return kind === "trigger" || kind === "reflectProjectile" || kind === "reflectEffect";
}

// ─── Tick step (3c) ────────────────────────────────────────────────────────

/**
 * Advance auras one tick.
 *
 * Contract (LOCKED \u2014 see file header + task brief):
 *   1. holders in SEAT order.
 *   2. per holder, auras in CANONICAL order (compareAuras).
 *   3. per aura, FIRST fire due periodic effects, THEN dispatch any
 *      `trigger { on: "onExpire" }` primitive on an aura about to naturally
 *      expire THIS tick, THEN expire.
 *      \u2014 "due" = `tick > appliedAtTick && imod(tick - appliedAtTick,
 *        intervalTicks) === 0` (no periodic on the cast/application tick).
 *      \u2014 "expire" = `expiresAtTick !== null && expiresAtTick <= tick`.
 *      Ordering means the expiry tick DOES fire its periodic effect then
 *      the aura is removed \u2014 Corruption ticks exactly 12 times.
 *   3a. #708: `onExpire` trigger dispatch \u2014 runs BEFORE removal (so its
 *      effect can still observe/mutate the holder's other auras \u2014 e.g. the
 *      Frostbolt shatter-timer's `dispel` consuming Frozen), in the SAME
 *      canonical order as the removal predicate below. Suppressed auras do
 *      not fire (mirrors the periodic gate). Because a given `AuraInstance`
 *      can only naturally expire ONCE (it is unconditionally dropped by this
 *      same pass, see 4 below), `everyN` needs no persisted counter \u2014 it
 *      fires iff `imod(1, everyN) === 0`, i.e. only ever for `everyN: 1`
 *      (content authors an onExpire trigger with `everyN: 1`; a larger value
 *      is schema-legal but can never fire, matching a one-shot event). The
 *      reacting effect targets the HOLDER ITSELF (source = target = seat) \u2014
 *      unlike the seat-addressed `dispatchTriggers` (#55) events, an
 *      `onExpire` trigger reacts to the HOLDER's own aura running out, not an
 *      interaction with another seat.
 *   4. Post-fire deaths cascade through `applyEffect`'s cleanup; a dead
 *      holder's remaining auras are LEFT ATTACHED (their expiresAtTick is
 *      moot until resurrection, which doesn't exist yet). Death cleanup
 *      idles the castState and clears moveIntent as usual.
 *
 * `applyEffectFn` is the shared effect router passed in from `step.ts`
 * (so this module never imports `effects.ts`). It must apply the given
 * effect to `draft[targetSeat]` and fire death cleanup as usual.
 *
 * `sink` (#463, optional — omitted is a byte-identical no-op) collects a
 * cosmetic `ccRemoved` event for every hard-CC aura this pass drops via
 * NATURAL EXPIRY (part (b) below) — the removal-side mirror of the
 * `ccApplied` onset callout `effects.ts` pushes. A periodic-triggered
 * break-on-damage removal (part (a), routed through `applyEffectFn` →
 * `applyEffect`) pushes its OWN `ccRemoved` event from `effects.ts` directly,
 * so it is never double-counted here.
 */
export function advanceAuras(
  draft: Entity[],
  bundle: ContentBundle,
  tick: Tick,
  applyEffectFn: ApplyEffectFn,
  sink?: CombatEventSink,
): void {
  // Build the interning table once from a snapshot of the pre-step state.
  // We use `draft` directly; iteration only reads the auras arrays and
  // periodic effects don't mutate defs, so the ordinal table stays stable
  // throughout this pass. Any newly-attached auras (from a periodic that
  // emits `applyAura` \u2014 not shipped, but reserved) do not participate in
  // THIS tick's iteration (their appliedAtTick == tick fails the periodic
  // gate).
  const ordinals = buildOrdinalsFromDraft(draft);

  for (let seat = 0; seat < draft.length; seat++) {
    const holder = draft[seat];
    /* c8 ignore next -- defensive: draft is dense. */
    if (holder === undefined) continue;
    if (holder.auras.length === 0) continue;

    // Canonical iteration snapshot \u2014 stable list even if we mutate
    // `draft[seat].auras` mid-loop (expire step below).
    const canonical = sortAuras(holder.auras, ordinals);

    // (a) apply-due-periodic \u2014 route through applyEffect. A periodic on
    // a suppressed aura contributes nothing.
    for (const aura of canonical) {
      // Refresh the holder reference each iteration \u2014 effects may have
      // mutated `draft[seat]` via cleanup (dead \u21d2 castState idle etc.).
      const cur = draft[seat];
      /* c8 ignore next -- defensive: seat is bounded. */
      if (cur === undefined) break;
      // Do not fire periodics on a dead holder \u2014 a corpse doesn't take
      // more DoT damage. Dead-flag check subsumes suppression + expiry.
      if (cur.hp === 0) break;
      // Suppression check uses the CURRENT holder (auras may have been
      // rearranged).
      if (isSuppressed(cur, aura, tick, bundle)) continue;
      // Expiry: `treatExpiryTickAsActive` \u2014 the boundary tick still
      // fires its periodic effect.
      if (aura.expiresAtTick !== null && tick > aura.expiresAtTick) continue;

      const def = bundle.auras.get(aura.def);
      /* c8 ignore next -- content invariant: applyAura validated the def. */
      if (def === undefined) continue;
      if (def.intervalTicks === undefined) continue;
      // Anchor to appliedAtTick: `tick > applied && (tick - applied) % interval == 0`.
      const delta = tick - aura.appliedAtTick;
      if (delta <= 0) continue;
      if (imod(delta, def.intervalTicks) !== 0) continue;

      // #182: a RESERVOIR-funded periodic pays out a DRAINING share of the
      // aura's `absorbRemaining` this firing (the collapse DoT: its per-tick
      // magnitude comes from the seeded pool, not the def's authored amount),
      // and the drained balance is written back to the instance. An ordinary
      // (non-reservoir) periodic fires `prim.effect` verbatim (pre-#182).
      const reservoir = hasReservoirPool(def);
      const payout = reservoir
        ? reservoirPerTick(aura, def.intervalTicks, tick)
        : null;
      for (const prim of def.primitives) {
        if (prim.kind !== "periodic") continue;
        let eff = prim.effect;
        if (payout !== null && (eff.kind === "damage" || eff.kind === "heal")) {
          eff = { ...eff, amount: asHp(payout.perTick) };
        } else if (
          // #320: cast-time-snapshot scaling — add `perPoint * <the combo-point
          // balance captured when this instance was (re)applied>` to the
          // authored magnitude, EVERY firing, never a live read (crossValidate
          // rejects pairing this with a reservoir payout on the same def, so
          // the two branches above/below never both apply to one primitive).
          prim.snapshotScaling !== undefined &&
          (eff.kind === "damage" || eff.kind === "heal")
        ) {
          const bonus = prim.snapshotScaling.perPoint * aura.pointsSnapshot;
          eff = { ...eff, amount: asHp(eff.amount + bonus) };
        }
        applyEffectFn(draft, aura.sourceSeat, seat, eff, tick);
      }
      // Write back the drained reservoir balance to the (still-present) instance.
      // The firing may have killed the holder (auras cleared) or dropped this
      // aura (break-on-damage) — match by identity and skip if gone.
      if (payout !== null && payout.nextRemaining !== aura.absorbRemaining) {
        drainReservoir(draft, seat, aura, payout.nextRemaining);
      }
    }

    // (b) expire. Refresh the holder reference \u2014 a periodic effect may
    // have killed / mutated it.
    const post = draft[seat];
    /* c8 ignore next -- defensive: seat is bounded. */
    if (post === undefined) continue;
    if (post.auras.length === 0) continue;
    // #463: cosmetic `ccRemoved` events for every aura THIS pass is about to
    // drop via natural expiry, walked in CANONICAL order (not raw array
    // order) so a same-tick multi-expiry fires its floaters in a fixed,
    // deterministic order \u2014 mirrors every other aura fan-out's iteration
    // discipline. A read-only pass over the SAME `expiresAtTick <= tick`
    // predicate the removal loop below applies independently; never touches
    // hashed state. `sink` omitted \u21d2 no-op, byte-identical to pre-#463.
    if (sink !== undefined) {
      for (const i of canonicalAuraOrder(post, draft)) {
        const a = post.auras[i];
        /* c8 ignore next -- index came from canonicalAuraOrder(post) \u21d2 in-bounds. */
        if (a === undefined) continue;
        if (a.expiresAtTick === null || a.expiresAtTick > tick) continue;
        const def = bundle.auras.get(a.def);
        /* c8 ignore next -- content invariant: applyAura validated the def. */
        if (def === undefined) continue;
        for (const ccClass of hardCcClasses(def.ccClasses)) {
          sink.push({
            kind: "ccRemoved",
            target: post.id,
            ccClass,
            source: a.sourceSeat,
          });
        }
      }
    }
    // #708: `onExpire` trigger dispatch \u2014 fires BEFORE the removal loop below
    // drops the expiring aura(s), same canonical order + `expiresAtTick <= tick`
    // predicate as that loop (single source of truth for "is this instance
    // naturally expiring this tick"). Refresh the holder reference every
    // iteration: an earlier onExpire effect this pass (e.g. a dispel) may have
    // mutated `draft[seat]`, including removing OTHER auras (never this loop's
    // own still-pending instances \u2014 identity-checked below).
    for (const i of canonicalAuraOrder(post, draft)) {
      const a = post.auras[i];
      /* c8 ignore next -- index came from canonicalAuraOrder(post) \u21d2 in-bounds. */
      if (a === undefined) continue;
      if (a.expiresAtTick === null || a.expiresAtTick > tick) continue;
      const cur = draft[seat];
      /* c8 ignore next -- defensive: seat is bounded. */
      if (cur === undefined) break;
      // A dead holder's expiring auras don't fire (mirrors the periodic gate;
      // an earlier onExpire trigger this pass could have killed the holder).
      if (cur.hp === 0) break;
      // Skip if an earlier onExpire trigger this pass already dropped THIS
      // exact instance (identity match) \u2014 no-op, not an error (e.g. a
      // still-pending sibling onExpire trigger racing a same-tick dispel).
      if (!identityPresent(cur.auras, a)) continue;
      if (isSuppressed(cur, a, tick, bundle)) continue;
      const def = bundle.auras.get(a.def);
      /* c8 ignore next -- content invariant: applyAura validated the def. */
      if (def === undefined) continue;
      for (const prim of def.primitives) {
        if (prim.kind !== "trigger") continue;
        if (prim.on !== "onExpire") continue;
        // See contract note 3a above: a one-shot natural expiry only ever
        // crosses the every-Nth boundary for `everyN: 1` (no persisted
        // counter \u2014 the instance is unconditionally dropped right after).
        if (imod(1, prim.everyN) !== 0) continue;
        applyEffectFn(draft, seat, seat, prim.effect, tick);
      }
    }
    let anyExpired = false;
    const kept: AuraInstance[] = [];
    // Re-read the holder: an onExpire trigger effect above may have mutated
    // `draft[seat]` (e.g. dispelled a DIFFERENT aura on the same holder), so
    // removal must walk the FRESHEST aura list, not the pre-trigger `post`
    // snapshot.
    const preRemoval = draft[seat];
    /* c8 ignore next -- defensive: seat is bounded. */
    if (preRemoval === undefined) continue;
    for (const a of preRemoval.auras) {
      if (a.expiresAtTick !== null && a.expiresAtTick <= tick) {
        anyExpired = true;
        continue;
      }
      kept.push(a);
    }
    if (anyExpired) {
      draft[seat] = { ...preRemoval, auras: kept };
    }
  }
}

/**
 * #708: identity (reference-equality) membership check \u2014 is `target` still
 * one of `list`'s elements? Used by the `onExpire` trigger dispatch to detect
 * whether an earlier trigger effect THIS pass already removed the instance
 * whose own expiry is being processed (a same-tick multi-onExpire race).
 */
function identityPresent(list: readonly AuraInstance[], target: AuraInstance): boolean {
  for (const a of list) {
    if (a === target) return true;
  }
  return false;
}

/**
 * #182: write the drained reservoir balance back onto the holder's matching
 * aura instance. The periodic firing may have mutated `draft[seat]` (death ⇒
 * auras cleared, break-on-damage ⇒ this aura dropped, breakOnDamage array
 * rebuild ⇒ new object references), so the instance is matched by its stable
 * identity `(def, sourceSeat, appliedAtTick)` in the CURRENT array and skipped
 * if gone. Deterministic (identity match, integer write). `independentInstances`
 * separates same-source instances by their distinct `appliedAtTick`, so the
 * match is unambiguous.
 */
function drainReservoir(
  draft: Entity[],
  seat: number,
  target: AuraInstance,
  nextRemaining: number,
): void {
  const cur = draft[seat];
  /* c8 ignore next -- defensive: seat bounded; a dead holder's auras are cleared. */
  if (cur === undefined || cur.auras.length === 0) return;
  let matched = false;
  const next: AuraInstance[] = [];
  for (const a of cur.auras) {
    if (
      !matched &&
      a.def === target.def &&
      a.sourceSeat === target.sourceSeat &&
      a.appliedAtTick === target.appliedAtTick
    ) {
      next.push({ ...a, absorbRemaining: nextRemaining });
      matched = true;
    } else {
      next.push(a);
    }
  }
  if (matched) draft[seat] = { ...cur, auras: next };
}

/**
 * Build the interning table from a mutable draft. Equivalent semantics
 * to `internAuraDefs(state)` but reads `draft` directly (no SimState
 * wrapper needed inside the aura step).
 */
function buildOrdinalsFromDraft(draft: readonly Entity[]): Map<string, number> {
  const seen = new Set<string>();
  for (const e of draft) {
    for (const a of e.auras) seen.add(a.def);
  }
  const ids: string[] = [];
  for (const id of seen) ids.push(id);
  ids.sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  const out = new Map<string, number>();
  for (let i = 0; i < ids.length; i++) {
    const id = ids[i];
    /* c8 ignore next -- defensive: ids is dense. */
    if (id === undefined) continue;
    out.set(id, i);
  }
  return out;
}
